const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

// Scroll in animation logic
function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute('data-cascade'))
          elementTarget.setAttribute('style', `--animation-order: ${index};`);
      }
      animatedElements.add(elementTarget);
      observer.unobserve(elementTarget);
    } else {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

let sharedScrollObserver = null;

// Global registry — accessible externally so morph or other code can trigger re-observation
window.scrollAnimationElements = new Set();

// Tracks elements that have already played their animation — morph reuses the same DOM node
// references so a WeakSet keyed by element identity survives attribute mutations
const animatedElements = new WeakSet();

function getScrollObserver() {
  if (!sharedScrollObserver) {
    sharedScrollObserver = new IntersectionObserver(onIntersection, {
      rootMargin: '0px 0px -50px 0px',
    });
  }
  return sharedScrollObserver;
}

function observeScrollElement(el) {
  const observer = getScrollObserver();
  observer.unobserve(el);
  observer.observe(el);
  window.scrollAnimationElements.add(el);
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  animationTriggerElements.forEach((el) => observeScrollElement(el));
}

function initializeScrollAnimationMutationObserver() {
  const mutationObserver = new MutationObserver((mutations) => {
    const elementsToObserve = new Set();

    mutations.forEach((mutation) => {
      // New nodes added to the DOM (section renders, AJAX)
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;

          if (node.classList.contains(SCROLL_ANIMATION_TRIGGER_CLASSNAME)) {
            elementsToObserve.add(node);
          }

          node.querySelectorAll(`.${SCROLL_ANIMATION_TRIGGER_CLASSNAME}`).forEach((el) => {
            elementsToObserve.add(el);
          });
        });
      }

      // Class attribute mutated by morph — handle re-added offscreen class
      if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
        const target = mutation.target;
        if (
          target instanceof Element &&
          target.classList.contains(SCROLL_ANIMATION_TRIGGER_CLASSNAME) &&
          target.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)
        ) {
          if (animatedElements.has(target)) {
            // Already animated — strip the offscreen class immediately, skip re-animation
            target.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
          } else {
            elementsToObserve.add(target);
          }
        }
      }
    });

    if (elementsToObserve.size === 0) return;

    // Defer to next frame so layout is settled before intersection is evaluated
    requestAnimationFrame(() => {
      elementsToObserve.forEach((el) => observeScrollElement(el));
    });
  });

  mutationObserver.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class'],
  });
}

// Zoom in animation logic
function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));

  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;

  animationTriggerElements.forEach((element) => {
    let elementIsVisible = false;
    const observer = new IntersectionObserver((elements) => {
      elements.forEach((entry) => {
        elementIsVisible = entry.isIntersecting;
      });
    });
    observer.observe(element);

    element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));

    window.addEventListener(
      'scroll',
      throttle(() => {
        if (!elementIsVisible) return;

        element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
      }),
      { passive: true }
    );
  });
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    // If we haven't reached the image yet
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    // If we've completely scrolled past the image
    return 100;
  }

  // When the image is in the viewport
  const distance = scrollY + viewportHeight - elementPositionY;
  let percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
  initializeScrollAnimationMutationObserver();
});

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => initializeScrollAnimationTrigger(event.target, true));
  document.addEventListener('shopify:section:reorder', () => initializeScrollAnimationTrigger(document, true));
}
